import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Modal, Popconfirm, Row } from 'antd';
import { TbFileDescription, TbFiles, TbHistory, TbLock, TbRestore, TbX } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { resourceActivityService } from '@/services/database';
import { BaseSkeleton, EmptySection, ResourceHeader } from '@/shared';
import { ResourceHistoryPage, ResourceHistoryPolicy, ResourceHistoryTarget, ResourceVersionListItem } from '@/types';

import HistorySider from './components/HistorySider';
import SnapshotPreview from './components/SnapshotPreview';
import { getResourceHistoryLabel, getResourceHistoryPluralLabel, HISTORY_MODAL_HEADER_HEIGHT } from './helpers';

interface ResourceHistoryModalProps {
    onClose: () => void;
    open: boolean;
    target?: ResourceHistoryTarget;
}

const ResourceHistoryModal: FC<ResourceHistoryModalProps> = ({ onClose, open, target }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const listRequestGeneration = useRef(0);
    const versionRequestGeneration = useRef(0);
    const [versions, setVersions] = useState<ResourceVersionListItem[]>([]);
    const [policy, setPolicy] = useState<ResourceHistoryPolicy>();
    const [selectedId, setSelectedId] = useState<string>();
    const [selectedVersion, setSelectedVersion] = useState<ResourceVersionListItem>();
    const [cursor, setCursor] = useState<string>();
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingVersion, setLoadingVersion] = useState(false);
    const [restoring, setRestoring] = useState(false);

    const reset = useCallback(() => {
        listRequestGeneration.current += 1;
        versionRequestGeneration.current += 1;
        setVersions([]);
        setPolicy(undefined);
        setSelectedId(undefined);
        setSelectedVersion(undefined);
        setCursor(undefined);
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        setLoadingVersion(false);
        setRestoring(false);
    }, []);

    const loadVersion = useCallback(
        async (version: ResourceVersionListItem) => {
            if (!target) {
                return;
            }

            setSelectedId(version.id);
            setSelectedVersion(undefined);
            const generation = ++versionRequestGeneration.current;

            if (version.locked) {
                setLoadingVersion(false);
                return;
            }

            setLoadingVersion(true);

            try {
                const detail = await resourceActivityService.getHistoryVersion(
                    target.resourceType,
                    target.resourceId,
                    version.id,
                );

                if (versionRequestGeneration.current === generation) {
                    setSelectedVersion(detail);
                }
            } catch (error) {
                captureException(error);
                toast.error('Unable to load this version');
            } finally {
                if (versionRequestGeneration.current === generation) {
                    setLoadingVersion(false);
                }
            }
        },
        [target],
    );

    const applyPage = useCallback(
        (page: ResourceHistoryPage, append: boolean) => {
            setVersions((current) => (append ? [...current, ...page.items] : page.items));
            setPolicy(page.policy);
            setCursor(page.nextCursor);
            setHasMore(page.hasMore);

            if (!append && page.items.length) {
                const preferred = page.items.find((version) => version.activityId === target?.activityId);
                const nextVersion = preferred ?? page.items.find((version) => !version.locked) ?? page.items[0];
                void loadVersion(nextVersion);
            }
        },
        [loadVersion, target?.activityId],
    );

    useEffect(() => {
        if (!open || !target) {
            return;
        }

        const generation = ++listRequestGeneration.current;
        setLoading(true);

        resourceActivityService
            .listHistory(target.resourceType, target.resourceId, { limit: 40 })
            .then((page) => {
                if (listRequestGeneration.current === generation) {
                    applyPage(page, false);
                }
            })
            .catch((error) => {
                captureException(error);
                toast.error('Unable to load version history');
            })
            .finally(() => {
                if (listRequestGeneration.current === generation) {
                    setLoading(false);
                }
            });

        return () => {
            listRequestGeneration.current += 1;
        };
    }, [applyPage, open, target]);

    const loadMore = async () => {
        if (!target || !cursor || loadingMore) {
            return;
        }

        setLoadingMore(true);
        try {
            const page = await resourceActivityService.listHistory(target.resourceType, target.resourceId, {
                cursor,
                limit: 40,
            });
            applyPage(page, true);
        } catch (error) {
            captureException(error);
            toast.error('Unable to load older versions');
        } finally {
            setLoadingMore(false);
        }
    };

    const restoreVersion = async () => {
        if (!target || !selectedId || selectedVersion?.locked) {
            return;
        }

        setRestoring(true);
        try {
            const restored = await resourceActivityService.restoreHistoryVersion(
                target.resourceType,
                target.resourceId,
                selectedId,
            );
            setVersions((current) => [restored, ...current.map((version) => ({ ...version }))]);
            setSelectedId(restored.id);
            setSelectedVersion(restored);
            window.dispatchEvent(
                new CustomEvent('resource-version-restored', {
                    detail: { resourceId: target.resourceId, resourceType: target.resourceType },
                }),
            );
            toast.success(`${getResourceHistoryLabel(target.resourceType)} restored`);
        } catch (error) {
            captureException(error);
            toast.error('Unable to restore this version');
        } finally {
            setRestoring(false);
        }
    };

    const selectedListVersion = versions.find((version) => version.id === selectedId);
    const locked = Boolean(selectedListVersion?.locked);
    const resourceLabel = target ? getResourceHistoryLabel(target.resourceType) : 'Resource';
    const resourcePluralLabel = target ? getResourceHistoryPluralLabel(target.resourceType) : 'Resources';
    const resourceIcon = target?.resourceType === 'memos' ? <TbFiles /> : <TbFileDescription />;

    return (
        <Modal
            destroyOnHidden
            centered
            open={open}
            width='min(92rem, calc(100vw - 4rem))'
            closable={false}
            closeIcon={null}
            footer={null}
            onCancel={onClose}
            afterOpenChange={(isOpen) => {
                if (!isOpen) {
                    reset();
                }
            }}
            styles={{
                body: { height: '56rem', maxHeight: 'calc(100vh - 4rem)', overflow: 'hidden' },
                content: { padding: 0, margin: 0, border: `1px solid ${token.colorBorder}`, overflow: 'hidden' },
            }}
        >
            <Row wrap={false} style={{ height: '100%' }}>
                <Col flex='20rem' style={{ height: '100%', minWidth: '18rem', maxWidth: '22rem' }}>
                    <HistorySider
                        hasMore={hasMore}
                        items={versions}
                        loading={loading}
                        loadingMore={loadingMore}
                        onLoadMore={() => void loadMore()}
                        onSelect={(version) => void loadVersion(version)}
                        policy={policy}
                        selectedId={selectedId}
                    />
                </Col>
                <Col flex='auto' style={{ display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%' }}>
                    <ResourceHeader
                        breadcrumbs={[
                            { icon: resourceIcon, label: resourcePluralLabel },
                            ...(target?.title ? [{ label: target.title }] : []),
                            { icon: <TbHistory />, label: 'Version History' },
                        ]}
                        rightActions={
                            <Button
                                type='text'
                                size='small'
                                aria-label='Close version history'
                                icon={<TbX size='1rem' />}
                                onClick={onClose}
                            />
                        }
                        style={{
                            boxSizing: 'border-box',
                            flex: 'none',
                            height: HISTORY_MODAL_HEADER_HEIGHT,
                            padding: '0.875rem 1rem',
                        }}
                    />
                    <main
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            padding: '1.5rem 2rem 1rem',
                            background: token.colorBgContainer,
                        }}
                    >
                        {loadingVersion ? (
                            <div
                                aria-label='Loading version preview'
                                style={{ maxWidth: '68rem', margin: '0 auto', paddingTop: '0.25rem' }}
                            >
                                <BaseSkeleton
                                    title={{ width: '28%' }}
                                    paragraph={{ rows: 12, width: ['100%', '96%', '100%', '84%'] }}
                                />
                            </div>
                        ) : locked ? (
                            <EmptySection
                                height='100%'
                                icon={<TbLock />}
                                label='Version Outside Retention Window'
                                description={`Your ${policy?.tier ?? 'current'} plan retains ${policy?.retentionDays ?? 0} days of version history.`}
                            />
                        ) : (
                            <SnapshotPreview resourceType={target?.resourceType ?? 'memos'} version={selectedVersion} />
                        )}
                    </main>
                    <footer
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '0.5rem',
                            padding: '0.75rem 1rem',
                            borderTop: `1px solid ${token.colorBorder}`,
                            background: token.colorBgElevated,
                        }}
                    >
                        <Button onClick={onClose}>Close</Button>
                        <Popconfirm
                            title={`Restore this ${resourceLabel.toLowerCase()} version?`}
                            description='The current state will be preserved as a new version before restoration.'
                            okText='Restore Version'
                            onConfirm={() => void restoreVersion()}
                        >
                            <Button
                                type='primary'
                                icon={<TbRestore />}
                                disabled={!selectedVersion || locked}
                                loading={restoring}
                            >
                                Restore Version
                            </Button>
                        </Popconfirm>
                    </footer>
                </Col>
            </Row>
        </Modal>
    );
};

export default ResourceHistoryModal;
